import React, { useState } from "react";
import { Modal, Spin } from "antd";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../../services/toast.config";

interface GuestAuthModalProps {
  show: boolean;
  onHide: () => void;
  companyId: string;
  companyType: string;
  onSuccess: (token: string) => void;
}

const GuestAuthModal: React.FC<GuestAuthModalProps> = ({
  show,
  onHide,
  companyId,
  companyType,
  onSuccess,
}) => {
  const { career_page_uuid } = useParams<{ career_page_uuid: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState<number | null>(
    null,
  );

  const handleSendOtp = async () => {
    if (!email) return notify.warn("Enter your email");
    setLoading(true);
    try {
      await recruitmentApi.sendGuestOtp(email, companyId, companyType);
      notify.success("OTP sent to your email!");
      setStep("otp");
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ||
          err?.data?.message ||
          err?.message ||
          "Failed to send otp!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return notify.warn("Enter OTP");
    setLoading(true);
    try {
      const res = await recruitmentApi.verifyGuestOtp(
        email,
        otp,
        companyId,
        companyType,
      );
      if (res?.token) {
        localStorage.setItem("guest_token", res.token);
        localStorage.setItem("company_id", companyId);
        localStorage.setItem("company_type", companyType);

        notify.success("Logged in! Redirecting to profile...");
        onSuccess(res.token);
        onHide();
        navigate(`/careers/${career_page_uuid || ""}/profile`);
      }
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ||
          err?.data?.message ||
          err?.message ||
          "Invalid OTP",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("email");
    setEmail("");
    setOtp("");
    setProfileCompletion(null);
    onHide();
  };

  return (
    <Modal
      open={show}
      onCancel={handleClose}
      footer={null}
      title={
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          <i className="ti ti-user-circle me-2" style={{ color: "#4f46e5" }} />
          Candidate Login
        </div>
      }
      centered
      styles={{ body: { padding: "8px 0 4px" } }}>
      <div style={{ padding: "8px 0 4px" }}>
        {profileCompletion !== null && profileCompletion < 50 && (
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 16,
            }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: "#92400e",
                marginBottom: 6,
              }}>
              <i className="ti ti-alert-triangle me-1" />
              Your profile is incomplete ({profileCompletion}%)
            </div>
            <p style={{ fontSize: 12, color: "#78350f", marginBottom: 10 }}>
              Complete your profile to apply faster and get better job matches.
            </p>
            <Link
              to={`/careers/${career_page_uuid || ""}/profile`}
              onClick={handleClose}
              style={{
                display: "inline-block",
                padding: "7px 16px",
                borderRadius: 6,
                background: "#f59e0b",
                color: "#fff",
                textDecoration: "none",
                fontSize: 12,
                fontWeight: 600,
              }}>
              <i className="ti ti-user-edit me-1" />
              Complete Profile
            </Link>
          </div>
        )}

        {step === "email" ? (
          <>
            <p style={{ color: "#6b7280", marginBottom: 16, fontSize: 14 }}>
              Login or create a profile to apply and track your applications.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  display: "block",
                  marginBottom: 6,
                }}>
                Email Address
              </label>
              <input
                type="email"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 7,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              />
            </div>
            <button
              style={{
                width: "100%",
                padding: "10px 0",
                borderRadius: 8,
                border: "none",
                background: "#4f46e5",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onClick={handleSendOtp}
              disabled={loading}>
              {loading ? (
                <Spin size="small" />
              ) : (
                <>
                  <i className="ti ti-mail" />
                  Send OTP
                </>
              )}
            </button>
          </>
        ) : (
          <>
            {profileCompletion === null && (
              <>
                <p style={{ color: "#6b7280", marginBottom: 16, fontSize: 14 }}>
                  Enter the 6-digit OTP sent to <strong>{email}</strong>
                </p>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      display: "block",
                      marginBottom: 6,
                    }}>
                    OTP Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: 7,
                      border: "1px solid #d1d5db",
                      fontSize: 24,
                      letterSpacing: 12,
                      textAlign: "center",
                      outline: "none",
                      boxSizing: "border-box",
                      fontWeight: 700,
                    }}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="——————"
                    onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                  />
                </div>
                <button
                  style={{
                    width: "100%",
                    padding: "10px 0",
                    borderRadius: 8,
                    border: "none",
                    background: "#4f46e5",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    marginBottom: 10,
                  }}
                  onClick={handleVerifyOtp}
                  disabled={loading}>
                  {loading ? (
                    <Spin size="small" />
                  ) : (
                    <>
                      <i className="ti ti-shield-check" />
                      Verify & Login
                    </>
                  )}
                </button>
                <div style={{ textAlign: "center" }}>
                  <button
                    style={{
                      background: "none",
                      border: "none",
                      color: "#6b7280",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setStep("email");
                      setOtp("");
                    }}>
                    ← Change Email
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default GuestAuthModal;
